import {useNavigate} from "react-router-dom";
import {
    Avatar,
    Box,
    Chip,
    CircularProgress,
    InputBase,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {
    Add as AddIcon,
    Search as SearchIcon,
    ManageAccountsOutlined as ManagerIcon,
    VerifiedUserOutlined as GrantedIcon,
    GroupsOutlined as AllTrainersIcon,
    EditOutlined as EditIcon,
    DeleteOutline as RevokeIcon,
} from "@mui/icons-material";
import {useEffect, useMemo, useState} from "react";

import {getManagers, revokeManager} from "../services/ManagerService";
import AddManagerDialog from "../componenets/managers/AddManagerDialog";
import {useMessageContext} from "../contexts/MessageContext";
import {useAuth} from "../contexts/AuthContext";
import {CAPABILITY_LABELS} from "../enums/Capabilities";
import {ConfirmDialog, ActionMenu} from "../componenets/ui";
import {colors, avatarPalette} from "../componenets/ui/colors";
import {t} from "../componenets/dashboard/coach/coachTokens";
import {KPI} from "../componenets/dashboard/coach/KpiRow";

/* ── Table cell presets (match Trainers/Clients) ──────────── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

const ManagerAvatar = ({name}) => {
    const initials = name?.split(" ").map(w => w[0]).join("").slice(0, 2) || "?";
    const idx = name ? name.charCodeAt(0) % avatarPalette.length : 0;
    return (
        <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
            <Avatar sx={{width: 36, height: 36, fontSize: "13px", fontWeight: 700, bgcolor: avatarPalette[idx], color: colors.white}}>
                {initials}
            </Avatar>
            <Typography sx={{fontSize: "13px", fontWeight: 600, color: colors.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                {name}
            </Typography>
        </Box>
    );
};

/* ═══════════════════════════════════════════════════════════
   Managers page — the delegated-authority persona (מנהל).
   Mirrors the Trainers page layout: title row on the canvas, KPI tiles,
   then a single card holding the filter bar and the table.
   ═══════════════════════════════════════════════════════════ */
const Managers = () => {
    const notify = useMessageContext()?.notificationHandler;
    const {isCompanyAdmin, isSuperAdmin} = useAuth();

    const [managers, setManagers] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [reload, setReload] = useState(false);
    const [keyword, setKeyword] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    const [revoking, setRevoking] = useState(null);

    const allowed = isCompanyAdmin() && !isSuperAdmin();

    useEffect(() => {
        if (!allowed) return;
        setLoading(true);
        getManagers()
            .then(res => setManagers(res?.data || []))
            .catch(() => setManagers([]))
            .finally(() => setLoading(false));
    }, [reload, allowed]);

    // The list is the small per-company manager set, so filtering in memory is cheap and avoids a
    // round trip per keystroke.
    const filtered = useMemo(() => {
        const kw = keyword.trim().toLowerCase();
        if (!kw) return managers;
        return managers.filter(m =>
            (m.name || "").toLowerCase().includes(kw) || (m.email || "").toLowerCase().includes(kw));
    }, [managers, keyword]);

    const stats = useMemo(() => ({
        total: managers.length,
        granted: managers.filter(m => (m.capabilities || []).length > 0).length,
        allTrainers: managers.filter(m => m.managesAllTrainers).length,
    }), [managers]);

    // The server enforces this independently; the early return only avoids rendering a screen whose
    // every call would 403.
    if (!allowed) {
        return (
            <Box sx={{padding: "24px"}}>
                <Typography sx={{fontSize: "13px", color: t.t2}}>אין לך הרשאה לצפות בעמוד זה</Typography>
            </Box>
        );
    }

    const handleRevoke = () => {
        const target = revoking;
        setRevoking(null);
        revokeManager(target.id)
            .then(() => notify?.success("ההרשאות בוטלו"))
            .then(() => setReload(prev => !prev))
            .catch(() => notify?.error("אירעה שגיאה בביטול ההרשאות"));
    };

    const scopeLabel = (m) => m.managesAllTrainers
        ? "כל המאמנים"
        : `${(m.managedTrainers || []).length} מאמנים נבחרים`;

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>
            <AddManagerDialog open={addOpen} close={() => setAddOpen(false)} setReload={setReload}/>

            <ConfirmDialog
                open={!!revoking}
                title="ביטול הרשאות מנהל"
                description={`לבטל את הרשאות הניהול של ${revoking?.name || ""}? המשתמש עצמו יישאר בחברה.`}
                confirmText="בטל הרשאות"
                onConfirm={handleRevoke}
                onClose={() => setRevoking(null)}
            />

            {/* ─── Title row (sits on canvas, no white band) ───── */}
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
                <Box>
                    <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
                        מנהלים
                    </Typography>
                    <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>
                        משתמשים עם חלק מיכולות מנהל החברה, מוגבלים למאמנים שבאחריותם
                    </Typography>
                </Box>
                <Box component="button" onClick={() => setAddOpen(true)} sx={{
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    background: t.green, color: "#fff",
                    height: 34, px: "15px", borderRadius: t.rs,
                    fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
                    border: "none", cursor: "pointer", fontFamily: "inherit",
                    transition: "all .14s",
                    "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
                }}>
                    <AddIcon sx={{fontSize: 16}}/>
                    מנהל חדש
                </Box>
            </Box>

            {/* ─── KPI grid (3 tiles) ───────────────────────── */}
            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(3, 1fr)"}, gap: "14px", flexShrink: 0}}>
                <KPI
                    icon={<ManagerIcon sx={{fontSize: 22}}/>} iconBg={t.greenLight} iconColor={t.green}
                    value={stats.total.toLocaleString()} valueColor={t.green}
                    label="סה״כ מנהלים"
                />
                <KPI
                    icon={<GrantedIcon sx={{fontSize: 22}}/>} iconBg={t.greenLight} iconColor={t.green}
                    badge="עם הרשאות" badgeBg={t.greenLight} badgeColor={t.greenText}
                    value={stats.granted.toLocaleString()} valueColor={t.green}
                    label="עם הרשאות"
                />
                <KPI
                    icon={<AllTrainersIcon sx={{fontSize: 22}}/>} iconBg={t.greenLight} iconColor={t.green}
                    value={stats.allTrainers.toLocaleString()} valueColor={t.text}
                    label="אחראים על כל המאמנים"
                />
            </Box>

            {/* ─── Table card (filters baked into top) ───────── */}
            <Box sx={{
                background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
                boxShadow: t.sh, overflow: "hidden",
                display: "flex", flexDirection: "column",
            }}>
                <Box sx={{
                    display: "flex", alignItems: "center", gap: "7px",
                    padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
                    background: t.bg, flexWrap: "wrap", flexShrink: 0,
                }}>
                    <Box sx={{
                        display: "inline-flex", alignItems: "center", gap: "4px",
                        fontSize: "12px", fontWeight: 700, color: t.greenText,
                        background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
                        padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
                    }}>
                        <ManagerIcon sx={{fontSize: 14}}/>
                        {filtered.length} מנהלים
                    </Box>

                    <Box sx={{position: "relative"}}>
                        <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
                        <InputBase
                            value={keyword}
                            onChange={e => setKeyword(e.target.value)}
                            placeholder="חיפוש לפי שם או אימייל..."
                            inputProps={{"aria-label": "חיפוש מנהלים"}}
                            sx={{
                                width: 220,
                                padding: "6px 10px 6px 30px",
                                border: `1.5px solid ${t.border}`,
                                borderRadius: t.rs,
                                background: t.white,
                                color: t.text,
                                fontSize: "12px",
                                "& input": {padding: 0},
                                "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
                            }}
                        />
                    </Box>

                    <Box sx={{flex: 1}}/>
                </Box>

                {/* Body */}
                {loading ? (
                    <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 320}}>
                        <CircularProgress size={32} sx={{color: t.green}}/>
                    </Box>
                ) : !filtered.length ? (
                    <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px"}}>
                        <Typography sx={{color: t.t3, fontSize: "13px", fontWeight: 500}}>
                            {keyword ? "אין מנהלים תואמים" : "אין מנהלים"}
                        </Typography>
                        <Typography sx={{color: t.t3, fontSize: "12px"}}>
                            {keyword
                                ? "נסה לשנות את החיפוש"
                                : "מנהל הוא משתמש עם חלק מיכולות מנהל החברה — בחר לו הרשאות ואת המאמנים שבאחריותו"}
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer sx={{overflowY: "auto", maxHeight: 540}}>
                        <Table stickyHeader sx={{tableLayout: "fixed"}}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{...th, textAlign: "right", width: 200}}>שם מלא</TableCell>
                                    <TableCell sx={{...th, textAlign: "right", width: 190, display: {xs: "none", md: "table-cell"}}}>אימייל</TableCell>
                                    <TableCell sx={{...th, textAlign: "right", width: 200}}>הרשאות</TableCell>
                                    <TableCell sx={{...th, textAlign: "center", width: 140}}>מאמנים באחריותו</TableCell>
                                    <TableCell sx={{...th, textAlign: "center", width: 90}}>פעולות</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtered.map(manager => (
                                    <TableRow key={manager.id} sx={{"&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}>
                                        <TableCell sx={{...td, textAlign: "right", overflow: "hidden"}}>
                                            <ManagerAvatar name={manager.name}/>
                                        </TableCell>
                                        <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                                            <Typography sx={{fontSize: "12px", color: t.t2, overflow: "hidden", textOverflow: "ellipsis"}}>
                                                {manager.email || "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{...td, textAlign: "right"}}>
                                            {(manager.capabilities || []).length === 0 ? (
                                                // Worth stating plainly: a manager with nothing ticked can do nothing at
                                                // all, which is easy to mistake for a broken account.
                                                <Typography sx={{fontSize: "12px", color: t.t3}}>ללא הרשאות</Typography>
                                            ) : (
                                                (manager.capabilities || []).map(c => (
                                                    <Chip
                                                        key={c}
                                                        label={CAPABILITY_LABELS[c] || c}
                                                        size="small"
                                                        sx={{fontSize: "11px", fontWeight: 600, height: 22, marginLeft: "4px",
                                                             bgcolor: t.greenLight, color: t.greenText}}
                                                    />
                                                ))
                                            )}
                                        </TableCell>
                                        <TableCell sx={{...td, textAlign: "center"}}>
                                            <Chip
                                                label={scopeLabel(manager)}
                                                size="small"
                                                sx={{fontSize: "11px", fontWeight: 600, height: 22,
                                                     bgcolor: manager.managesAllTrainers ? t.greenLight : `${colors.info}15`,
                                                     color: manager.managesAllTrainers ? t.greenText : colors.info}}
                                            />
                                        </TableCell>
                                        <TableCell sx={{...td, textAlign: "center"}}>
                                            <ActionMenu
                                                actions={[
                                                    {
                                                        label: "עריכת הרשאות",
                                                        icon: <EditIcon fontSize="small"/>,
                                                        onClick: () => navigate(`/app/managers/${manager.id}/permissions`),
                                                    },
                                                    {
                                                        label: "ביטול הרשאות",
                                                        icon: <RevokeIcon fontSize="small"/>,
                                                        danger: true,
                                                        onClick: () => setRevoking(manager),
                                                    },
                                                ]}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Box>
    );
};

export default Managers;
